import { getUrlParam } from 'core-components/helpers/url'
import { MAIN_PAGE_ID } from 'core-components/sitebuilder/layout/constants/sitePages'
import { IRole } from 'core-components/sitebuilder/models/IRole'
import { ISitePage } from 'core-components/sitebuilder/models/ISitePage'
import { IUser } from 'core-components/sitebuilder/models/IUser'
import { IWidgetsData } from 'core-components/sitebuilder/models/IWidgetData'

export const sitePageInitialData: ISitePage = {
  id: '',
  isNew: false as boolean,
  root: '',
  parent: '',
  childrenIds: [] as string[],
  childrenVisibleIds: [] as string[],
  name: '',
  groupName: '',
  type: 'Static',
  iconClass: '',
  order: 0,
  redirectTo: '',
  audience: '',
  url: '',
  redirectUrl: '',
  redirectOption: '',
  redirectWindow: '',
  redirectWithParams: false,
  layout: '',
  layoutSettings: {} as any,
  layoutUser: '', // user's choice
  layoutUserSettings: {} as any,
  defaultSettings: {} as any,
  hidden: 0,
  disabled: 0,
  visible: true,
  params: {} as any,
  availableWidgetsData: [] as IWidgetsData[],
  widgetsData: [] as IWidgetsData[],
  roleIds: [] as string[],
  rolesMode: 'Included',
  hasChildren: false,
}

export const sortItems = (items: ISitePage[]): ISitePage[] => {
  return [...items].sort((a, b) => {
    if (a.order > b.order) {
      return 1
    }
    if (a.order < b.order) {
      return -1
    }
    if (a.name > b.name) {
      return 1
    }
    if (a.name < b.name) {
      return -1
    }
    return 0
  })
}

export const getSortedChildren = (
  itemOrId: ISitePage | string,
  allItems: ISitePage[]
): ISitePage[] => {
  const result: ISitePage[] = []
  const sitePage = typeof itemOrId === 'string' ? allItems.find((i) => i.id === itemOrId) : itemOrId

  if (sitePage) {
    sitePage.childrenIds.forEach((id) => {
      const child = allItems.find((i) => i.id === id)
      if (child) {
        result.push(child)
      }
    })
  }

  return result.length > 1 ? sortItems(result) : result
}

export const sitePageUrl = (item: ISitePage) => {
  const locale = getUrlParam('locale')
  const urlLocale = locale ? `/locale/${locale}` : ''

  if (item.redirectOption === 'Redirect to another site' && item.redirectUrl) {
    return item.redirectUrl
  }

  if (item.redirectOption === 'Inner redirect' && item.redirectTo) {
    return `${urlLocale}/page/${item.redirectTo}`
  }

  return item.url || `${urlLocale}/page/${item.id}`
}

export const createSitePage = (data?: Partial<ISitePage>): ISitePage => ({
  ...sitePageInitialData,
  params: {},
  availableWidgetsData: [],
  widgetsData: [],
  roleIds: [],
  ...data,
})

const getRoleHomePage = (sitePages: ISitePage[], user: IUser, roles: IRole[]) => {
  const userRoles = user.roleIds
    .map((id) => roles.find((role) => role.id === id))
    .filter(Boolean) as IRole[]
  const userMainRole = userRoles.sort((a, b) => a.order - b.order)[0]

  if (userMainRole) {
    const roleHomePage = userMainRole.homePageId
    if (roleHomePage) {
      return sitePages.find((page) => page.id === roleHomePage) || null
    }
  }
  return null
}

export const getRootPage = (sitePages: ISitePage[]) => {
  return sitePages.find((i) => i.id === MAIN_PAGE_ID)
}

export const getHomePage = (sitePages: ISitePage[], user: IUser, roles: IRole[]) => {
  const roleHomePage = getRoleHomePage(sitePages, user, roles)

  if (roleHomePage) {
    return roleHomePage
  }

  const generalPages = sitePages.filter((el) => {
    return el.parent === MAIN_PAGE_ID && !el.hidden
  })

  const pageForSearch = generalPages.length ? generalPages : sitePages

  return pageForSearch.reduce((a, b) => {
    return b.order < a.order ? b : a
  }, pageForSearch[0])
}

export const getDefaultSitePage = (
  sitePages: ISitePage[],
  user: IUser,
  roles: IRole[]
): ISitePage | null => {
  const generalRoot = getHomePage(sitePages, user, roles)
  if (generalRoot) {
    if (!generalRoot.redirectTo && !generalRoot.redirectUrl && !generalRoot.hidden) {
      return generalRoot
    }
    const children = getSortedChildren(generalRoot, sitePages)
    return children[0] || generalRoot
  }
  return sitePages[0]
}
